import { Injectable } from '@angular/core';
import { PropertyStateService } from '../state/property-state.service';
import { CalculatorService, ICalculatorField, ICalculatorSection } from './calculator.service';
import { CurrencyPipe } from '@angular/common';
import { CalculatorBoxComponent } from '../components';

@Injectable()
export class RentalAnalysisService {
    public refinanceMode: 'cash' | 'finance' = 'cash';

    constructor(
        private state: PropertyStateService,
        private calculator: CalculatorService,
        private currencyPipe: CurrencyPipe
    ) {}

    init() {
        const state = this.state;

        this.calculator.setFieldValuesFromParseObject([state.rentalAnalysis_sharedTopBox, state.rentalAnalysis_cashBox, state.rentalAnalysis_financeBox, state.rentalAnalysis_refinanceBox], state.saRental);
    }

    calculate() {
        this.updateCashBox();
        this.updateFinanceBox();
        this.updateRefinanceBox();
    }

    // inputs are disabled on Flyer Full Analysis
    disableFormFields() {
        const state = this.state;

        [state.rentalAnalysis_sharedTopBox, state.rentalAnalysis_cashBox, state.rentalAnalysis_financeBox, state.rentalAnalysis_refinanceBox].forEach((box: ICalculatorSection[]) => {
            box.forEach((section: ICalculatorSection) => {
                section.fields.forEach((field: ICalculatorField) => {
                    if (field.formControl) {
                        field.formControl.disable();
                    }
                });
            });
        });
    }

    enableFormFields() {
        const state = this.state;

        [state.rentalAnalysis_sharedTopBox, state.rentalAnalysis_cashBox, state.rentalAnalysis_financeBox, state.rentalAnalysis_refinanceBox].forEach((box: ICalculatorSection[]) => {
            box.forEach((section: ICalculatorSection) => {
                section.fields.forEach((field: ICalculatorField) => {
                    if (field.formControl) {
                        field.formControl.enable();
                    }
                });
            });
        });
    }

    setRefinanceToggle(box: CalculatorBoxComponent) {
        const state = this.state;

        // If Finance Box inputs have been fully filled out we switch the refinance box into Finance mode
        // Otherwise we disable the toggle
        const downPayment = this.calculator.getField(state.rentalAnalysis_financeBox, 'down-payment').inputValue;
        const points = this.calculator.getField(state.rentalAnalysis_financeBox, 'points').inputValue;
        const rate = this.calculator.getField(state.rentalAnalysis_financeBox, 'loan-rate').inputValue;
        const balloon = this.calculator.getField(state.rentalAnalysis_financeBox, 'balloon-month').inputValue;
        if (
            this.calculator.isInputUnset(points) ||
            this.calculator.isInputUnset(rate) ||
            this.calculator.isInputUnset(balloon) ||
            this.calculator.isInputUnset(downPayment)
        ) {
            this.refinanceMode = 'cash';
            box ? box.refinanceModeSwitch.emit('disable') : () => { };
        } else {
            // Switch to finance
            this.refinanceMode = 'finance';
            box ? box.refinanceModeSwitch.emit('finance') : () => { };
        }

        this.updateRefinanceBox();
    }

    setAnnualTaxes() {
        const state = this.state;

        if (state.estatedInfo.has('taxes') && state.estatedInfo.get('taxes')[0] && state.estatedInfo.get('taxes')[0].amount) {
            const tax = state.estatedInfo.get('taxes')[0].amount;
            this.calculator.getField(state.rentalAnalysis_cashBox, 'less-taxes').formControl?.setValue(this.currencyPipe.transform(tax));
            this.calculator.getField(state.rentalAnalysis_cashBox, 'less-taxes').inputValue = Number(tax);
        }
    }

    updateCashBox() {
        const state = this.state;

        this.calculator.getField(state.rentalAnalysis_cashBox, 'simo-cc').outputValue = this.calcSimoCC();
        this.calculator.getField(state.rentalAnalysis_cashBox, 'acqui-cost').outputValue = this.calcAcqui();
        this.calculator.getField(state.rentalAnalysis_cashBox, 'rent').outputValue = this.calcRent();
        this.calculator.getField(state.rentalAnalysis_cashBox, 'gross-income').outputValue = this.calcGrossIncome();
        this.calculator.getField(state.rentalAnalysis_cashBox, 'gross-return').outputValue = this.calcGrossReturn();
        this.calculator.getField(state.rentalAnalysis_cashBox, 'man-fee').outputValue = this.calcManFee();
        this.calculator.getField(state.rentalAnalysis_cashBox, 'less-vacancy').outputValue = this.calcLessVacancy();
        this.calculator.getField(state.rentalAnalysis_cashBox, 'less-hoa').outputValue = this.calcLessHOA();
        this.calculator.getField(state.rentalAnalysis_cashBox, 'total-expenses').outputValue = this.calcTotalExpenses();
        this.calculator.getField(state.rentalAnalysis_cashBox, 'net-income').outputValue = this.calcNetIncome();
        this.calculator.getField(state.rentalAnalysis_cashBox, 'cash-flow').outputValue = this.calcCashFlow();
        this.calculator.getField(state.rentalAnalysis_cashBox, 'cap-rate').outputValue = this.calcCapRate();
        this.calculator.getField(state.rentalAnalysis_cashBox, 'plus-equity').outputValue = this.calcPlusEquity();

        const insuranceField = this.calculator.getField(state.rentalAnalysis_cashBox, 'less-insurance');
        if (!insuranceField.inputValue) {
            const estimate = this.calcAnnualInsuranceEstimate();
            insuranceField.formControl?.setValue(this.currencyPipe.transform(estimate));
        }
    }

    updateFinanceBox() {
        const state = this.state;

        this.calculator.getField(state.rentalAnalysis_financeBox, 'down-payment').outputValue = this.calculateDownPayment();
        this.calculator.getField(state.rentalAnalysis_financeBox, 'amount-financed').outputValue = this.calculateAmountFinanced();
        this.calculator.getField(state.rentalAnalysis_financeBox, 'points-paid').outputValue = this.calculateLoanPointsPaid();
        this.calculator.getField(state.rentalAnalysis_financeBox, 'annual-loan-payment').outputValue = this.calculateLoanPaymentsDebtService();
        this.calculator.getField(state.rentalAnalysis_financeBox, 'monthly-loan-payment').outputValue = this.calculateMonthlyLoanPayment();
        this.calculator.getField(state.rentalAnalysis_financeBox, 'total-payment').outputValue = this.calculateTotalPayment();
        this.calculator.getField(state.rentalAnalysis_financeBox, 'annual-net').outputValue = this.calculateAnnualNet();
        this.calculator.getField(state.rentalAnalysis_financeBox, 'cash-flow').outputValue = this.calculateNetCashFlow();
        this.calculator.getField(state.rentalAnalysis_financeBox, 'cash-out-of-pocket').outputValue = this.calculateTotalCashOutOfPocket();
        this.calculator.getField(state.rentalAnalysis_financeBox, 'cash-on-cash').outputValue = this.calculateCashOnCash();
    }

    updateRefinanceBox() {
        const state = this.state;

        if (this.refinanceMode === 'finance') {
            this.calculator.getField(state.rentalAnalysis_refinanceBox, 'refi-amount').outputValue = this.calcRefiLoanAmount();
            this.calculator.getField(state.rentalAnalysis_refinanceBox, 'refi-cc').outputValue = this.calcRefiCCWithFees();
            this.calculator.getField(state.rentalAnalysis_refinanceBox, 'refi-monthly-payment').outputValue = this.calcRefiMonthlyPayment();
            this.calculator.getField(state.rentalAnalysis_refinanceBox, 'refi-cash-out').outputValue = this.calcRefiCashOut();
            this.calculator.getField(state.rentalAnalysis_refinanceBox, 'net-income-after-refi').outputValue = this.calcNetIncomeAfterRefi();
            this.calculator.getField(state.rentalAnalysis_refinanceBox, 'adjusted-net-cash').outputValue = this.calcRefiMonthlyCashFlow();
            this.calculator.getField(state.rentalAnalysis_refinanceBox, 'leveraged-cap').outputValue = this.calcLeveragedCapRate();
            this.calculator.getField(state.rentalAnalysis_refinanceBox, 'remaining-net-equity').outputValue = this.calcRemainingNetEquity();
        } else {
            // Cash Calculations
            this.calculator.getField(state.rentalAnalysis_refinanceBox, 'refi-amount').outputValue = this.calcRefiLoanAmount();
            this.calculator.getField(state.rentalAnalysis_refinanceBox, 'refi-cc').outputValue = this.calcRefiCCWithFees();
            this.calculator.getField(state.rentalAnalysis_refinanceBox, 'refi-monthly-payment').outputValue = this.calcRefiMonthlyPayment();
            this.calculator.getField(state.rentalAnalysis_refinanceBox, 'refi-cash-out').outputValue = this.calcRefiCashOut_CASH();
            this.calculator.getField(state.rentalAnalysis_refinanceBox, 'net-income-after-refi').outputValue = this.calcNetIncomeAfterRefi();
            this.calculator.getField(state.rentalAnalysis_refinanceBox, 'adjusted-net-cash').outputValue = this.calcRefiMonthlyCashFlow();
            this.calculator.getField(state.rentalAnalysis_refinanceBox, 'leveraged-cap').outputValue = this.calcLeveragedCapRate_CASH();
            this.calculator.getField(state.rentalAnalysis_refinanceBox, 'remaining-net-equity').outputValue = this.calcRemainingNetEquity_CASH();
        }
    }

    calcRemainingNetEquity() {
        const arv = this.calculator.getField(this.state.rentalAnalysis_sharedTopBox, 'arv').inputValue as number;
        const refiAmount = this.calcRefiLoanAmount() as number;
        const totalExpenses = this.calculateTotalPayment() as number;
        const refiCC = this.calcRefiCCWithFees() as number;
        const simoCC = this.calcSimoCC() as number;
        const renoCost = this.calculator.getField(this.state.rentalAnalysis_sharedTopBox, 'reno-cost').inputValue || 0;

        // Require these values to be set for calculation
        if (
            this.calculator.isInputUnset(arv) ||
            this.calculator.isInputUnset(refiAmount) ||
            this.calculator.isInputUnset(totalExpenses) ||
            this.calculator.isInputUnset(refiCC) ||
            this.calculator.isInputUnset(simoCC) ||
            this.calculator.isInputUnset(renoCost)
        ) {
            return null;
        }

        return arv - refiAmount - totalExpenses - refiCC - simoCC - renoCost;
    }

    // This is for CASH mode only
    calcRemainingNetEquity_CASH() {
        const arv = this.calculator.getField(this.state.rentalAnalysis_sharedTopBox, 'arv').inputValue as number;
        const refiAmount = this.calcRefiLoanAmount() as number;
        const refiCC = this.calcRefiCCWithFees() as number;
        const simoCC = this.calcSimoCC() as number;
        const renoCost = this.calculator.getField(this.state.rentalAnalysis_sharedTopBox, 'reno-cost').inputValue || 0;

        // Require these values to be set for calculation
        if (
            this.calculator.isInputUnset(arv) ||
            this.calculator.isInputUnset(refiAmount) ||
            this.calculator.isInputUnset(refiCC) ||
            this.calculator.isInputUnset(simoCC) ||
            this.calculator.isInputUnset(renoCost)
        ) {
            return null;
        }

        return arv - refiAmount - refiCC - simoCC - renoCost;
    }

    calcLeveragedCapRate() {
        const cashOutOfPocket = this.calculateTotalCashOutOfPocket() as number;
        const refiCC = this.calcRefiCCWithFees() as number;
        const incomeAfterRefi = this.calcNetIncomeAfterRefi() as number;

        // Require these values to be set for calculation
        if (
            this.calculator.isInputUnset(cashOutOfPocket) ||
            this.calculator.isInputUnset(refiCC) ||
            this.calculator.isInputUnset(incomeAfterRefi)
        ) {
            return null;
        }

        return incomeAfterRefi / (cashOutOfPocket + refiCC);
    }

    // This is for CASH mode only
    calcLeveragedCapRate_CASH() {
        const totalAcquiCost = this.calcAcqui() as number;
        const refiCC = this.calcRefiCCWithFees() as number;
        const incomeAfterRefi = this.calcNetIncomeAfterRefi() as number;

        // Require these values to be set for calculation
        if (
            this.calculator.isInputUnset(totalAcquiCost) ||
            this.calculator.isInputUnset(refiCC) ||
            this.calculator.isInputUnset(incomeAfterRefi)
        ) {
            return null;
        }

        return incomeAfterRefi / (totalAcquiCost + refiCC);
    }

    calcRefiMonthlyCashFlow() {
        const net = this.calcNetIncomeAfterRefi() as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(net)) {
            return null;
        }

        return net / 12;
    }

    calcNetIncomeAfterRefi() {
        const netIncome = this.calcNetIncome();
        const monthlyPayment = this.calcRefiMonthlyPayment() as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(netIncome) || this.calculator.isInputUnset(monthlyPayment)) {
            return null;
        }

        return netIncome - (monthlyPayment * 12);
    }

    calcRefiCashOut() {
        const refiAmount = this.calcRefiLoanAmount() as number;
        const amountFinanced = this.calculateAmountFinanced() as number;
        const refiCC = this.calcRefiCCWithFees() as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(refiAmount) || this.calculator.isInputUnset(amountFinanced) || this.calculator.isInputUnset(refiCC)) {
            return null;
        }

        return refiAmount - amountFinanced - refiCC;
    }

    // This is for CASH mode only
    calcRefiCashOut_CASH() {
        const refiAmount = this.calcRefiLoanAmount() as number;
        const refiCC = this.calcRefiCCWithFees() as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(refiAmount) || this.calculator.isInputUnset(refiCC)) {
            return null;
        }

        return refiAmount - refiCC;
    }

    calcRefiMonthlyPayment() {
        const refiAmount = this.calcRefiLoanAmount() as number;
        const rate = this.calculator.getField(this.state.rentalAnalysis_refinanceBox, 'refi-rate').inputValue as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(refiAmount) || this.calculator.isInputUnset(rate)) {
            return null;
        }

        return (refiAmount + (refiAmount * rate)) * (1.45 / 240);
    }

    calcRefiCCWithFees() {
        const refi = this.calcRefiLoanAmount() as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(refi)) {
            return null;
        }

        return 0.04 * refi;
    }

    calcRefiLoanAmount() {
        const ltv = this.calculator.getField(this.state.rentalAnalysis_refinanceBox, 'bank-ltv').inputValue as number;
        const arv = this.calculator.getField(this.state.rentalAnalysis_sharedTopBox, 'arv').inputValue as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(ltv) || this.calculator.isInputUnset(arv)) {
            return null;
        }

        return arv * ltv;
    }

    calcSimoCC() {
        const purchasePrice = this.calculator.getField(this.state.rentalAnalysis_sharedTopBox, 'purchase-price').inputValue as number;
        const closingPercent = this.calculator.getField(this.state.rentalAnalysis_cashBox, 'simo-cc').inputValue as number;

        return purchasePrice * closingPercent;
    }

    calcAcqui() {
        const purchasePrice = this.calculator.getField(this.state.rentalAnalysis_sharedTopBox, 'purchase-price').inputValue as number;
        const simoCC = this.calcSimoCC();
        const renoCost = this.calculator.getField(this.state.rentalAnalysis_sharedTopBox, 'reno-cost').inputValue as number;
        return purchasePrice + simoCC + renoCost;
    }

    calcRent() {
        return 12 * (this.calculator.getField(this.state.rentalAnalysis_cashBox, 'rent-monthly').inputValue as number);
    }

    calcGrossIncome() {
        return this.calcRent();
    }

    calcGrossReturn() {
        return this.calcGrossIncome() / this.calcAcqui();
    }

    calcManFee() {
        return this.calcGrossIncome() * (this.calculator.getField(this.state.rentalAnalysis_cashBox, 'man-fee').inputValue as number);
    }

    calcLessVacancy(): number {
        return 0.1 * this.calcGrossIncome();
    }

    calcLessHOA(): number {
        return 12 * (this.calculator.getField(this.state.rentalAnalysis_cashBox, 'hoa-monthly').inputValue as number);
    }

    calcTotalExpenses(): number {
        const manFee = this.calcManFee() || 0;
        const manReserve = this.calculator.getField(this.state.rentalAnalysis_cashBox, 'man-reserve').inputValue || 0;
        const vacancy = this.calcLessVacancy() || 0;
        const pest = this.calculator.getField(this.state.rentalAnalysis_cashBox, 'less-pest').inputValue || 0;
        const taxes = this.calculator.getField(this.state.rentalAnalysis_cashBox, 'less-taxes').inputValue || 0;
        let insurance = this.calculator.getField(this.state.rentalAnalysis_cashBox, 'less-insurance').inputValue as number;

        if (!insurance) {
            insurance = this.calcAnnualInsuranceEstimate();
        }

        const hoa = this.calcLessHOA() || 0;
        return manFee + manReserve + vacancy + pest + taxes + insurance + hoa;
    }

    calcNetIncome(): number {
        return this.calcGrossIncome() - this.calcTotalExpenses();
    }

    calcCashFlow(): number {
        return this.calcNetIncome() / 12;
    }

    calcCapRate(): number {
        return this.calcNetIncome() / this.calcAcqui();
    }

    calcPlusEquity(): number {
        const arv = this.calculator.getField(this.state.rentalAnalysis_sharedTopBox, 'arv').inputValue as number;
        return arv - this.calcAcqui();
    }

    calculateDownPayment() {
        const downPayment = this.calculator.getField(this.state.rentalAnalysis_financeBox, 'down-payment').inputValue as number;
        const price = this.calculator.getField(this.state.rentalAnalysis_sharedTopBox, 'purchase-price').inputValue as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(downPayment) || this.calculator.isInputUnset(price)) {
            return null;
        }

        return downPayment * price;
    }

    calculateAmountFinanced() {
        const downPayment = this.calculator.getField(this.state.rentalAnalysis_financeBox, 'down-payment').inputValue as number;
        const price = this.calculator.getField(this.state.rentalAnalysis_sharedTopBox, 'purchase-price').inputValue as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(downPayment) || this.calculator.isInputUnset(price)) {
            return null;
        }

        return (1 - downPayment) * price;
    }

    calculateLoanPointsPaid() {
        const financed = this.calculateAmountFinanced() as number;
        const points = this.calculator.getField(this.state.rentalAnalysis_financeBox, 'points').inputValue as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(financed) || this.calculator.isInputUnset(points)) {
            return null;
        }

        return financed * points;
    }

    calculateLoanPaymentsDebtService() {
        const balloonMonths = this.calculator.getField(this.state.rentalAnalysis_financeBox, 'balloon-month').inputValue as number;
        const loanPayments = this.calculateMonthlyLoanPayment() as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(balloonMonths) || this.calculator.isInputUnset(loanPayments)) {
            return null;
        }

        return balloonMonths * loanPayments;
    }

    /**
     * @deprecated: This calculation was replaced with this.calculateLoanPaymentsDebtService()
     * in Deal Underwriter (4).xls
     */
    calculateAnnualLoanPayment() {
        return this.calculateLoanPaymentsDebtService();
        // const amountFinanced = this.calculateAmountFinanced();
        // const loanRate = this.calcUtils.getField(this.financeBox, 'loan-rate').inputValue;
        //
        // return amountFinanced * loanRate;
    }

    calculateMonthlyLoanPayment() {
        const amountFinanced = this.calculateAmountFinanced() as number;
        const loanRate = this.calculator.getField(this.state.rentalAnalysis_financeBox, 'loan-rate').inputValue as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(amountFinanced) || this.calculator.isInputUnset(loanRate)) {
            return null;
        }

        return (amountFinanced * loanRate) / 12;
    }

    calculateTotalPayment() {
        const loanPayments = this.calculateLoanPaymentsDebtService() as number;
        const pointsPaid = this.calculateLoanPointsPaid() as number;
        const downPayment = this.calculateDownPayment() as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(loanPayments) || this.calculator.isInputUnset(pointsPaid) || this.calculator.isInputUnset(downPayment)) {
            return null;
        }

        return loanPayments + pointsPaid + downPayment;
    }

    calculateAnnualNet() {
        const net = this.calcNetIncome() as number;
        const total = this.calculateTotalPayment() as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(net) || this.calculator.isInputUnset(total)) {
            return null;
        }

        return net - total;
    }

    calculateNetCashFlow() {
        const net = this.calculateAnnualNet() as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(net)) {
            return null;
        }

        return net / 12;
    }

    calculateTotalCashOutOfPocket() {
        const renoCost = this.calculator.getField(this.state.rentalAnalysis_sharedTopBox, 'reno-cost').inputValue as number;
        const totalExpenses = this.calcTotalExpenses() as number;
        const simoCC = this.calcSimoCC() as number;
        const totalPayment = this.calculateTotalPayment() as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(totalPayment) || this.calculator.isInputUnset(simoCC) || this.calculator.isInputUnset(totalExpenses) || this.calculator.isInputUnset(renoCost)) {
            return null;
        }

        return renoCost + totalPayment + totalExpenses + simoCC;
    }

    calculateCashOnCash() {
        const net = this.calculateAnnualNet() as number;
        const outOfPockit = this.calculateTotalCashOutOfPocket() as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(net) || this.calculator.isInputUnset(outOfPockit)) {
            return null;
        }

        return net / outOfPockit;
    }

    calcAnnualInsuranceEstimate() {
        const monthlyInsuranceEstimate = (this.calculator.getField(this.state.rentalAnalysis_sharedTopBox, 'arv').inputValue as number) * 0.00035;
        const holdTime = 12;

        return monthlyInsuranceEstimate * holdTime;
    }
}
